/**
 * `HNS_SERVICE_FACTS` — the ONE place product code reads an S8.0 live-probe outcome from (S8 plan r3 / r4, Task 8.2).
 * Every value is the verdict of a NOTES «S8: live-probed facts» row (2026-09-25, `jamsqlazsthns`), cited per field.
 * Frozen; consumers read `hnsServiceFacts()` so unit tests can run every consumer against BOTH values of each fact
 * (`withHnsFacts`, `azureStorageHnsFacts.testSupport.ts`) — the shipped configuration is never the only one exercised.
 */
export type HnsServiceFacts = {
  /** M1 / M5 / M13: SDK `move()` (legacy mode) + `If-None-Match: *` refuses every existing destination; per-segment-encoded names round-trip. */
  moveImplementation: 'sdk' | 'raw-legacy' | 'raw-posix' | 'disabled';
  /** M14: no continuation was ever returned (10,110 descendants), so re-sending a dispatched continuation is unproven. */
  renameReplaySafe: boolean;
  /** M14: not observable for the same reason. */
  sourceRootPresentUntilDone: boolean;
  /** M8: a renamed file and a renamed directory keep ETag, creation time and Last-Modified (also across file systems). */
  renameKeepsEtag: { file: boolean; directory: boolean };
  /** A6: listing, blob HEAD and getAccessControl carry the same ETag value (the listing unquoted). */
  listedEtagEqualsHeadEtag: boolean;
  /** R4: re-sending a batch from its start token is accepted and converges (applied + returned, response lost, aborted mid-application). */
  recursiveAclReplaySafe: boolean;
  /** R8: `If-Match` on a recursive ACL request → 400 UnsupportedHeader. */
  recursiveAclHonoursIfMatch: boolean;
  /** R9: the next batch token after the root was deleted and re-created applies to the NEW tree. */
  rootReplacedMidJob: 'refused' | 'applies-to-new' | 'unknown';
  /** A11 / R9: `x-ms-creation-time` survives ACL writes (single and recursive) and rename, changes on delete + re-create. */
  creationTimeIdentity: boolean;
  /** R3: children of a failed directory are visited only when the caller can list it — otherwise silently skipped. */
  failedDirectoryChildrenVisited: boolean;
  /** R5 (ii) / (iv): a recursive modify that carries an explicit mask keeps it. */
  recursiveModifyKeepsExplicitMask: boolean;
  /** R5 (vi): a recursive remove recalculates the mask of every scope it removes an entry from (it cannot carry one). */
  recursiveRemoveRecalculatesMask: boolean;
  /** R6 (i) / A9: an access-only set keeps directories' default entries recursively, removes them on a single path. */
  accessOnlySetKeepsDefaults: { recursive: boolean; singlePath: boolean };
  /** R6 (iii)–(v): default entries in a recursive change are ignored on files (no failure). */
  defaultEntriesOnFiles: 'ignored' | 'failed';
  /** R7: a token continues after a new client and after 5 / 15 minutes. */
  tokenSurvivesReconnect: boolean;
};

export const HNS_SERVICE_FACTS: Readonly<HnsServiceFacts> = Object.freeze({
  moveImplementation: 'sdk',
  renameReplaySafe: false,
  sourceRootPresentUntilDone: false,
  renameKeepsEtag: Object.freeze({ file: true, directory: true }),
  listedEtagEqualsHeadEtag: true,
  recursiveAclReplaySafe: true,
  recursiveAclHonoursIfMatch: false,
  rootReplacedMidJob: 'applies-to-new',
  creationTimeIdentity: true,
  failedDirectoryChildrenVisited: false,
  recursiveModifyKeepsExplicitMask: true,
  recursiveRemoveRecalculatesMask: true,
  accessOnlySetKeepsDefaults: Object.freeze({ recursive: true, singlePath: false }),
  defaultEntriesOnFiles: 'ignored',
  tokenSurvivesReconnect: true,
});

let active: Readonly<HnsServiceFacts> = HNS_SERVICE_FACTS;

/** The facts in force: `HNS_SERVICE_FACTS`, except inside a test's `withHnsFacts` body. */
export function hnsServiceFacts(): Readonly<HnsServiceFacts> {
  return active;
}

/** Test seam behind `withHnsFacts` (testSupport); `undefined` restores the shipped facts. */
export function setHnsServiceFactsForTests(next?: Readonly<HnsServiceFacts>): void {
  active = next ?? HNS_SERVICE_FACTS;
}
